import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';

const articleDetailImages: Record<string, string> = {
  'art-001': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20geometric%20shapes%2C%20minimalist%20corporate%20style%2C%20professional%20technology%20platform%20banner%2C%20clean%20editorial%20header%20image&width=1200&height=600&seq=article-detail-platform-overview&orientation=landscape',
  'art-002': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20flowing%20shapes%2C%20minimalist%20registration%20portal%20banner%2C%20user%20friendly%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-registration-guide&orientation=landscape',
  'art-003': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20security%20geometric%20shapes%2C%20minimalist%20login%20authentication%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-login-methods&orientation=landscape',
  'art-004': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20search%20discovery%20shapes%2C%20minimalist%20search%20feature%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-search-guide&orientation=landscape',
  'art-005': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20cream%20gradients%2C%20soft%20data%20analytics%20shapes%2C%20minimalist%20dashboard%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-data-center&orientation=landscape',
  'art-006': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20cybersecurity%20shapes%2C%20minimalist%20security%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-security&orientation=landscape',
  'art-007': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20speed%20performance%20shapes%2C%20minimalist%20tech%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-performance&orientation=landscape',
  'art-008': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20mobile%20device%20shapes%2C%20minimalist%20mobile%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-mobile-guide&orientation=landscape',
  'art-009': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20settings%20shapes%2C%20minimalist%20account%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-account-settings&orientation=landscape',
  'art-010': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20payment%20transaction%20shapes%2C%20minimalist%20fintech%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-payment&orientation=landscape',
  'art-011': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20customer%20support%20shapes%2C%20minimalist%20service%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-customer-service&orientation=landscape',
  'art-012': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20update%20log%20shapes%2C%20minimalist%20changelog%20banner%2C%20professional%20style%2C%20clean%20editorial%20header&width=1200&height=600&seq=article-detail-changelog&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!slug) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const related = getRelatedArticles(article.slug, article.categorySlug);
  const articleUrl = `https://qiaojzx.cn/article/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Organization', name: article.author, url: 'https://qiaojzx.cn' },
    datePublished: article.publishDate,
    url: articleUrl,
    inLanguage: 'zh-CN',
    publisher: { '@type': 'Organization', name: 'qiaojzx.cn', url: 'https://qiaojzx.cn' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
  };

  const breadcrumbItems = [
    { '@type': 'ListItem' as const, position: 1, name: '首页', item: 'https://qiaojzx.cn' },
  ];
  if (category) {
    breadcrumbItems.push({
      '@type': 'ListItem' as const,
      position: 2,
      name: category.name,
      item: `https://qiaojzx.cn/category/${category.slug}`,
    });
  }
  breadcrumbItems.push({
    '@type': 'ListItem' as const,
    position: category ? 3 : 2,
    name: article.title,
    item: articleUrl,
  });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - qiaojzx.cn 官方平台`}
        description={article.summary}
        keywords={`qiaojzx.cn,${article.tags.join(',')},${article.title}`}
        canonicalUrl={articleUrl}
        ogType="article"
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <main className="pt-20 pb-16 md:pb-24">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-foreground-400 text-xs">{article.readTime}</span>
              <span className="text-foreground-400 text-xs">{article.publishDate}</span>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-5 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 pb-6 border-b border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-primary-600"></i>
              </div>
              <div>
                <div className="text-foreground-800 text-sm font-medium">{article.author}</div>
                <div className="text-foreground-400 text-xs">qiaojzx.cn 官方认证作者</div>
              </div>
            </div>
          </header>

          <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
            <img
              src={articleDetailImages[article.id]}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-base md:prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-800
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
              prose-ul:my-5 prose-li:text-foreground-600 prose-li:leading-relaxed
              prose-li:marker:text-primary-400"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-2 mt-10 pt-6 border-t border-background-200/70 flex-wrap">
            <span className="text-foreground-400 text-xs">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </article>

        {related.length > 0 && (
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-16">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {related.slice(0, 4).map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.slug}`}
                  className="group flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={articleDetailImages[rel.id]}
                      alt={rel.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-foreground-800 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {rel.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-foreground-400 text-xs">{rel.readTime}</span>
                      <span className="text-foreground-400 text-xs">{rel.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}